import React, { useState, useEffect, useRef } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Modal from "react-bootstrap/Modal";
import InputGroup from "react-bootstrap/InputGroup";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaCheck, FaTimes, FaCaretUp, FaTimesCircle } from "react-icons/fa";
import "./PaymentForm.css";
import {
    removeNonAsciiSpecialCharacters,
    processAddressField,
    processCommentField,
    processCityField
} from "../Utils";
import parse from "html-react-parser";
import govEmailConfig from "./govEmailConfig.json";

// Stripe imports
import { loadStripe } from "@stripe/stripe-js";
import {
    EmbeddedCheckoutProvider,
    EmbeddedCheckout
} from "@stripe/react-stripe-js";

// FIXED: Better error handling for Stripe initialization with more robust checking
const getStripePromise = () => {
    const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
    if (!key) {
        return null;
    }

    try {
        return loadStripe(key);
    } catch (error) {
        return null;
    }
};

// Load Stripe outside component to avoid recreating on every render
const stripePromise = getStripePromise();

// FIXED: Improved CSS for Stripe rendering
const stripeStyles = `
.embedded-checkout-container {
    min-height: 650px !important;
    height: 650px !important;
    width: 100% !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
    background: transparent !important;
    display: block !important;
}

iframe.embedded-checkout {
    height: 650px !important;
    min-height: 650px !important;
    width: 100% !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    visibility: visible !important;
    overflow: visible !important;
    background: transparent !important;
    display: block !important;
    opacity: 1 !important;
    z-index: 9999 !important;
    position: relative !important;
}

#stripe-embedded-container {
    height: 650px !important;
    min-height: 650px !important;
    width: 100% !important;
    border: none !important;
    margin: 0 !important;
    padding: 0 !important;
    visibility: visible !important;
    display: block !important;
    opacity: 1 !important;
    z-index: 9999 !important;
    position: relative !important;
}
`;

// PaymentForm component
function PaymentForm({ webpageData, data, closeForm, ticketType, onPaymentSuccess, showCloseButton = false }) {
    const { eventName } = useParams();
    const location = useLocation();

    // Create ref for the form
    const formRef = useRef(null);
    // Create a ref for the main container to control scrolling
    const mainContainerRef = useRef(null);

    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState();
    const [formLoading, setFormLoading] = useState(false);
    const [transactionId, setTransactionId] = useState("");
    const [discountCode, setDiscountCode] = useState("");
    const [validated, setValidated] = useState(false);
    const [step2Validated, setStep2Validated] = useState(false); // Separate validation state for step 2
    const [consentValue, setConsentValue] = useState(true);
    const [showTicketClickToMore, setShowClickToMore] = useState(true);
    const [showTicketClickToPhoto, setShowClickToPhoto] = useState(true);
    const [discountCodeValid, setDiscountCodeValid] = useState(false);
    const [showModal, setShow] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showStep1SuccessModal, setShowStep1SuccessModal] = useState(false);
    const [showStep1FailModal, setShowStep1FailModal] = useState(false);
    const [showFailModal, setShowFailModal] = useState(false);
    const [hideForm, setHideForm] = useState(false);
    const [stripeSessionId, setStripeSessionId] = useState(null);
    const [stripePaymentIntentId, setStripePaymentIntentId] = useState(null);
    const [basicDetails, setBasicDetails] = useState({
        members: [{ name: "", email: "", phone: "", comment: "" }],
        category: [ticketType === "General Admission" ? "General Admission" : "VIP"]
    });


    const [isFreeRegistration, setIsFreeRegistration] = useState(false);
    const [showNote, setShowNote] = useState(true);
    const [promoCodeMessage, setPromoCodeMessage] = useState("");
    // Stripe states
    const [clientSecret, setClientSecret] = useState(null);
    const [showStripeCheckout, setShowStripeCheckout] = useState(false);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [paymentIntentId, setPaymentIntentId] = useState(null);
    const [stripeInitialized, setStripeInitialized] = useState(false);
    const [stripeError, setStripeError] = useState(null);
    const [stripeReady, setStripeReady] = useState(false);
    const [isCheckoutComplete, setIsCheckoutComplete] = useState(false);
    const [leadStorageAttempted, setLeadStorageAttempted] = useState(false);
    const [showValidationMessage, setShowValidationMessage] = useState(false);
    const [applyClicked, setApplyClicked] = useState(false);
    const [isCheckingCode, setIsCheckingCode] = useState(false);

    // NEW: Add payment monitoring states
    const [paymentMonitorActive, setPaymentMonitorActive] = useState(false);
    const [stripeInstance, setStripeInstance] = useState(null);

    // Define missing variables
    const [selectedTickets, setSelectedTickets] = useState([ticketType || "General Admission"]);
    const [selectedTier, setSelectedTier] = useState("");
    const [tierValidationMessage, setTierValidationMessage] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");

    const bannerData = webpageData?.banner || {}
    const successData = webpageData?.formMessages?.paymentSuccess || {};
    // Define ticket prices
    const ticketPrices = {
        "General Admission": bannerData?.eventDetails?.GA || 199,
        "VIP": bannerData?.eventDetails?.VIP || 349,
    };


    const couponCodes = data?.codes?.discountCodes
        ? [...data.codes.discountCodes, "TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"]
        : ["TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"];


    const [discountCodeTouched, setDiscountCodeTouched] = useState(false);
    const [showGovMessage, setShowGovMessage] = useState(false);

    const [isValidPromo, setIsValidPromo] = useState(null);

    useEffect(() => {
        const leadSource = bannerData?.eventDetails?.leadSource || "USSAIC-2025";

        const checkPromo = async () => {
            if (discountCode) {
                const isValid = await validatePromoCode(discountCode, leadSource);
                setIsValidPromo(isValid);
            }
        };

        checkPromo();
    }, [discountCode, bannerData]);

    const handleDiscountCodeBlur = () => {
        setDiscountCodeTouched(true);

        const validCodes = ["TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"];

        if (validCodes.includes(discountCode.trim())) {
            setDiscountCodeValid(true);
        } else {
            setDiscountCodeValid(false);
        }
    };

    const handleApplyClick = async () => {
        setApplyClicked(true);
        setIsCheckingCode(true);
        setDiscountCodeTouched(true);
        setShowValidationMessage(true);
        setShowNote(false);

        const leadSource = 'USSAIC-2025';
        const codeUpper = discountCode.toUpperCase();

        const result = await validatePromoCode(codeUpper, leadSource);
        setPromoCodeMessage(result.message);
        setIsValidPromo(result?.isValid === true);
        // console.log(result, "result")
       

        if (result?.isValid) {
            setDiscountCodeValid(true);
        } else {
            setDiscountCodeValid(false);
    
            
            setPromoCodeMessage(
                result.message
            );
        }

        setIsCheckingCode(false);
    };


    // Check if current date is within government email promotion period
    const isWithinGovEmailPeriod = () => {
        const now = new Date();
        const startDate = new Date(webpageData?.govEmailPromotion.startDate);
        const endDate = new Date(webpageData?.govEmailPromotion.endDate);
        return now >= startDate && now <= endDate;
    };

    // Check if email has government domain and within promotion period
    const isGovernmentEmail = (email) => {
        if (!email) return false;
        const emailLower = email.toLowerCase();
        return emailLower.endsWith('.gov') && isWithinGovEmailPeriod();
    };

    const validateDiscountCode = () => {
        setDiscountCodeTouched(true);

        const isValid =
            couponCodes.includes(discountCode) ||
            ["TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"].includes(discountCode) ||
            data?.tickets?.eventPaid === "false" ||
            eventName === "ussaic2025";

        setDiscountCodeValid(isValid);
    };

    
   
    const validatePromoCode = async (code, leadSource) => {
        const section = basicDetails.category[0];
    
        try {
            const response = await axios.post(
                process.env.REACT_APP_API_VALIDATION_PROMOCODE,
                { promoCode: code, leadSource, section },
                { headers: { 'Content-Type': 'application/json' } }
            );
    
            const isValid = response?.data?.data === true;
            const isMessage = response?.data?.message
            
            if (code === "RHYTHM25" && isValid) {
                return {
                    isValid: true,
                    isFree: false,
                    discountPercentage: 25,
                    message: `Promo code "${code}" is valid! You get a 25% discount on your registration.`
                };
            }
    
            // Default: treat all other codes as free (legacy behavior)
            if (isValid) {
                return {
                    isValid: true,
                    isFree: true,
                    discountPercentage: 100,
                    message: isMessage
                };
            }
    
            return {
                isValid: false,
                isFree: false,
                discountPercentage: 0,
                message: isMessage
            };
    
        } catch (err) {
            return {
                isValid: false,
                isFree: false,
                discountPercentage: 0,
                message: 'Error validating promo code. Please try again later.'
            };
        }
    };
    

    


    const reducePromoCodeCount = async (code, leadSource, count) => {
        const section = basicDetails.category[0];
        try {
            await fetch(process.env.REACT_APP_API_REDUCTION_PROMOCODE, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    promoCode: code,
                    leadSource: leadSource,
                    count: count,
                    section: section
                })
            });
        } catch (error) {
        }
    };


    // FIXED: Better validation of Stripe.js loading
    useEffect(() => {

        if (stripePromise) {
            setStripeReady(true);

            // Initialize Stripe instance for direct payment monitoring
            stripePromise.then(stripe => {
                if (stripe) {
                    setStripeInstance(stripe);
                }
            });
        } else {
            setStripeReady(false);
            setStripeError("Payment system failed to load. Please refresh the page and try again.");
        }
    }, []);

    // Add Stripe CSS to the document
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = stripeStyles;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    // NEW: Enhanced payment success monitoring with multiple detection methods
    useEffect(() => {
        if (paymentMonitorActive && paymentIntentId && stripeInstance) {
            let pollCount = 0;
            const maxPolls = 60;

            const pollForPaymentSuccess = async () => {
                try {
                    pollCount++;

                    // Retrieve the payment intent to check its status
                    const { paymentIntent } = await stripeInstance.retrievePaymentIntent(clientSecret);


                    if (paymentIntent.status === 'succeeded') {
                        setPaymentMonitorActive(false);

                        // Try to get session ID from the payment intent metadata or other sources
                        let sessionId = null;
                        if (paymentIntent.metadata && paymentIntent.metadata.checkout_session_id) {
                            sessionId = paymentIntent.metadata.checkout_session_id;
                        } else if (stripeSessionId) {
                            sessionId = stripeSessionId;
                        }

                        triggerSuccessFlow(paymentIntent.id, sessionId);
                        return;
                    }

                    // Continue polling if not succeeded and under max attempts
                    if (pollCount < maxPolls && paymentIntent.status !== 'canceled') {
                        setTimeout(pollForPaymentSuccess, 2000);
                    } else {
                        setPaymentMonitorActive(false);
                    }

                } catch (error) {
                    if (pollCount < maxPolls) {
                        setTimeout(pollForPaymentSuccess, 2000);
                    } else {
                        setPaymentMonitorActive(false);
                    }
                }
            };

            const pollTimer = setTimeout(pollForPaymentSuccess, 1000);

            return () => {
                clearTimeout(pollTimer);
                setPaymentMonitorActive(false);
            };
        }
    }, [paymentMonitorActive, paymentIntentId, stripeInstance, clientSecret, stripeSessionId]);


    // NEW: Additional useEffect to handle scrolling when Stripe checkout becomes visible
    useEffect(() => {
        if (showStripeCheckout && step === 2) {
            // Stripe checkout just became visible, aggressively scroll to top
            scrollToTop();
            setTimeout(scrollToTop, 50);
            setTimeout(scrollToTop, 100);
            setTimeout(scrollToTop, 200);
            setTimeout(scrollToTop, 500);
            setTimeout(scrollToTop, 1000);
        }
    }, [showStripeCheckout, step]);

    // FIXED: Function to ensure form is scrolled to the top when moving between steps
    const scrollToTop = () => {
        // Only scroll within the modal/form container, NOT the entire page
        const performScroll = () => {
            // Method 1: Scroll main container to view first
            if (mainContainerRef.current) {
                mainContainerRef.current.scrollIntoView({
                    behavior: 'instant',
                    block: 'start',
                    inline: 'start'
                });
            }

            // Method 2: Try to scroll to the specific element by ID (only if it's within our form)
            const targetElement = document.getElementById('payment-modal-top');
            if (targetElement) {
                // Only scroll the element itself, not the whole page
                targetElement.scrollTop = 0;
                targetElement.scrollLeft = 0;
            }

            // Method 3: Find and scroll form-specific containers only
            const formContainers = document.querySelectorAll('.main-div, .form-container-primary, .form-container-secondary, .stripe-payment-section');
            formContainers.forEach(el => {
                if (el.scrollTo) {
                    el.scrollTo(0, 0);
                }
                el.scrollTop = 0;
                el.scrollLeft = 0;
            });

            // Method 4: Scroll any modal containers specifically
            const modalElements = document.querySelectorAll('.modal-body, .modal-content');
            modalElements.forEach(el => {
                if (el.scrollTo) {
                    el.scrollTo(0, 0);
                }
                el.scrollTop = 0;
                el.scrollLeft = 0;
            });

            // REMOVED: window.scrollTo() calls that affect the background page
            // REMOVED: document.body.scrollTop and document.documentElement.scrollTop
            // These were causing the background page to scroll to top
        };

        // Perform scroll immediately
        performScroll();

        // Multiple timed attempts to override any layout shifts
        setTimeout(performScroll, 1);
        setTimeout(performScroll, 10);
        setTimeout(performScroll, 25);
        setTimeout(performScroll, 50);
        setTimeout(performScroll, 100);
        setTimeout(performScroll, 200);
    };

    // FIXED: Improved auto-initialization for Stripe checkout with proper timing
    useEffect(() => {
        // When step changes to 2, automatically initialize the payment
        if (step === 2) {
            // AGGRESSIVE: Immediately scroll to top when step 2 starts - before any DOM changes
            scrollToTop();

            // Reset any existing payment state to ensure a fresh start
            setClientSecret(null);
            setPaymentIntentId(null);
            setShowStripeCheckout(false);
            setStripeError(null);
            setIsCheckoutComplete(false);
            setLeadStorageAttempted(false);
            setPaymentMonitorActive(false);

            // AGGRESSIVE: Scroll again immediately after state resets
            setTimeout(scrollToTop, 1);
            setTimeout(scrollToTop, 10);
            setTimeout(scrollToTop, 25);

            // Add a slight delay to ensure DOM is ready
            const timer = setTimeout(() => {
                // Scroll before Stripe initialization
                scrollToTop();

                if (stripeReady && !clientSecret && !paymentProcessing) {
                    setPaymentProcessing(true);
                    setStripeInitialized(true);

                    // Scroll right before making the API call
                    setTimeout(scrollToTop, 50);

                    initializeStripeCheckout()
                        .then(success => {
                            setPaymentProcessing(false);

                            // Scroll immediately after Stripe initialization
                            scrollToTop();
                            setTimeout(scrollToTop, 50);
                            setTimeout(scrollToTop, 100);
                            setTimeout(scrollToTop, 200);

                            // Start payment monitoring after successful initialization
                            if (success) {
                                setTimeout(() => {
                                    setPaymentMonitorActive(true);
                                    // Final scroll after everything is loaded
                                    setTimeout(scrollToTop, 100);
                                    setTimeout(scrollToTop, 300);
                                    setTimeout(scrollToTop, 500);
                                }, 2000);
                            }
                        })
                        .catch(err => {
                            setStripeInitialized(false);
                            setPaymentProcessing(false);
                            setStripeError(err.message || "Failed to initialize payment");
                            // Scroll to top even on error
                            scrollToTop();
                            setTimeout(scrollToTop, 100);
                        });
                }
            }, 100); // Reduced delay for faster response

            return () => clearTimeout(timer);
        }

        // For any step change, ensure we're at the top
        if (step === 1) {
            // Small delay for step 1 as well
            setTimeout(scrollToTop, 25);
            setTimeout(scrollToTop, 100);
        }
    }, [step, stripeReady]);

    // FIXED: Trigger success flow - the central function to handle successful payments
    const triggerSuccessFlow = (paymentId, sessionId = null) => {
        // Prevent duplicate triggers
        if (isCheckoutComplete && leadStorageAttempted) {
            return;
        }


        // Stop any active monitoring
        setPaymentMonitorActive(false);

        // Update state to indicate payment is complete
        setIsCheckoutComplete(true);

        // Hide Stripe checkout
        setShowStripeCheckout(false);

        // Store the payment IDs
        if (paymentId) {
            setStripePaymentIntentId(paymentId);
            setPaymentIntentId(paymentId);
        }
        if (sessionId) {
            setStripeSessionId(sessionId);
            setTransactionId(sessionId);
        }

        // Force hide all Stripe elements
        hideStripeElements();

        // Attempt to store leads with both IDs
        if (!leadStorageAttempted) {
            setLeadStorageAttempted(true);
            storePaymentSuccessLeads(paymentId, sessionId);
        }

        // Show success modal
        setShowSuccessModal(true);
        setHideForm(true);

        // Call the success callback if provided
        if (onPaymentSuccess) {
            onPaymentSuccess(paymentId, sessionId);
        }
    };

    // NEW: Helper function to hide all Stripe elements
    const hideStripeElements = () => {
        try {
            // Force hide the Stripe elements using multiple methods
            document.querySelectorAll('#stripe-embedded-container, #checkout, .embedded-checkout-container, iframe.embedded-checkout')
                .forEach(el => {
                    if (el) {
                        el.style.cssText = "display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; width: 0 !important; position: absolute !important; pointer-events: none !important; overflow: hidden !important; z-index: -999 !important;";
                    }
                });

            // Try to completely remove the Stripe iframe if possible
            const iframes = document.querySelectorAll('iframe.embedded-checkout');
            iframes.forEach(iframe => {
                try {
                    if (iframe && iframe.parentNode) {
                        iframe.parentNode.removeChild(iframe);
                    }
                } catch (e) {
                }
            });
        } catch (error) {
        }
    };

    // NEW: Improved lead storage function
    // FIXED: Lead storage function with correct payment format and amount for backend
    const storePaymentSuccessLeads = async (paymentIntentId, sessionId) => {

        setFormLoading(true);

        try {
            const firstMember = basicDetails.members[0];

            const firstFullName = firstMember.name || '';
            let firstName = "";
            let lastName = "";

            if (firstFullName && firstFullName.includes(" ")) {
                const nameParts = firstFullName.split(" ");
                firstName = nameParts[0];
                lastName = nameParts.slice(1).join(" ");
            } else {
                firstName = firstFullName;
                lastName = "";
            }


            const eventDetails = {
                name: bannerData?.eventDetails?.formEventName || "USSAIC",
                date: bannerData?.eventDetails?.eventDate || "July 8, 2025",
                time: bannerData?.eventDetails?.eventTime || "9 AM - 5 PM ET",
                address: bannerData?.eventDetails?.formFullAddress || "",
                fromEmailAddress: bannerData?.eventDetails?.fromEmailAddress || "confirmation@celligence.com"
            };


            const firstSection = basicDetails.category[0] || "General Admission";

            // FIXED: Calculate transaction amount
            const transactionAmount = calculateTotalAmount();

            // FIXED: Create comprehensive payment info comment with correct ID identification and amount
            let paymentInfoComment = `Payment completed - Amount: $${transactionAmount.toFixed(2)}`;

            // Correctly identify Payment Intent ID (starts with 'pi_') vs Session ID (starts with 'cs_')
            if (paymentIntentId && paymentIntentId.startsWith('pi_')) {
                paymentInfoComment += `, Stripe Payment Intent ID: ${paymentIntentId}`;
            } else if (paymentIntentId) {
                // If it doesn't start with 'pi_', it might be mislabeled
                if (paymentIntentId.startsWith('cs_')) {
                    paymentInfoComment += `, Stripe Session ID: ${paymentIntentId}`;
                } else {
                    paymentInfoComment += `, Stripe Transaction ID: ${paymentIntentId}`;
                }
            }

            if (sessionId && sessionId.startsWith('cs_')) {
                paymentInfoComment += `, Stripe Session ID: ${sessionId}`;
            } else if (sessionId && sessionId.startsWith('pi_')) {
                paymentInfoComment += `, Stripe Payment Intent ID: ${sessionId}`;
            } else if (sessionId) {
                paymentInfoComment += `, Stripe Transaction ID: ${sessionId}`;
            }


            // Add payment info to first member's comment
            const firstMemberComment = firstMember.comment
                ? `${firstMember.comment}. ${paymentInfoComment}`
                : paymentInfoComment;

            // First attendee request body with payment information
            const firstRequestBody = {
                event_payment_done: "1",
                firstName: firstName,
                lastName: lastName,
                phone: firstMember.phone || "",
                email: firstMember.email || "",
                comments: firstMemberComment,
                referrerSource: bannerData?.eventDetails?.leadSource || "USSAIC-2025",
                leadSource: bannerData?.eventDetails?.leadSource || "USSAIC-2025",
                TCPA_Consent_Given: consentValue,
                attendee_desc: "yes",
                section: firstSection,
                eventDetails: eventDetails,
                opted_for_text_message: 0
            };

            // Process first attendee with payment
            const firstResponse = await axios.post(
                process.env.REACT_APP_API_EMAIL,
                firstRequestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                    timeout: 30000
                }
            );

            // Process remaining attendees with payment reference
            const successfulRegistrations = [0];
            const failedRegistrations = [];

            if (basicDetails.members.length > 1) {
                const remainingMembers = basicDetails.members.slice(1);

                // FIXED: Create comprehensive payment reference comment for additional attendees with amount
                let groupPaymentReference = `Group booking with ${firstName} (${firstMember.email}). Amount: $${transactionAmount}.00`;

                // Correctly identify Payment Intent ID vs Session ID for group reference
                if (paymentIntentId && paymentIntentId.startsWith('pi_')) {
                    groupPaymentReference += `, Stripe Payment Intent ID: ${paymentIntentId}`;
                } else if (paymentIntentId) {
                    if (paymentIntentId.startsWith('cs_')) {
                        groupPaymentReference += `, Stripe Session ID: ${paymentIntentId}`;
                    } else {
                        groupPaymentReference += `, Stripe Transaction ID: ${paymentIntentId}`;
                    }
                }

                if (sessionId && sessionId.startsWith('cs_')) {
                    groupPaymentReference += `, Stripe Session ID: ${sessionId}`;
                } else if (sessionId && sessionId.startsWith('pi_')) {
                    groupPaymentReference += `, Stripe Payment Intent ID: ${sessionId}`;
                } else if (sessionId) {
                    groupPaymentReference += `, Stripe Transaction ID: ${sessionId}`;
                }

                const commonComment = `Paid via Stripe. ${groupPaymentReference}. Booked as part of group with primary contact: ${firstName} ${lastName || ''} (${firstMember.email})`;

                // Process each remaining attendee SEQUENTIALLY
                for (let idx = 0; idx < remainingMembers.length; idx++) {
                    const member = remainingMembers[idx];
                    const i = idx + 1;

                    let success = false;
                    let attempts = 0;
                    const maxAttempts = 2;

                    while (!success && attempts < maxAttempts) {
                        attempts++;
                        try {
                            const fullName = member.name || '';
                            let memberFirstName = "";
                            let memberLastName = "";

                            if (fullName && fullName.includes(" ")) {
                                const nameParts = fullName.split(" ");
                                memberFirstName = nameParts[0];
                                memberLastName = nameParts.slice(1).join(" ");
                            } else {
                                memberFirstName = fullName;
                                memberLastName = "";
                            }

                            const section = basicDetails.category[i] || "General Admission";

                            // FIXED: Add comprehensive payment details to the comments for this attendee
                            const attendeeComment = member.comment
                                ? `${member.comment}. ${commonComment}`
                                : commonComment;

                            // Additional attendee request body with payment reference
                            const additionalOrderPaymentRequest = {
                                additionalAttendee: true,
                                primaryPaymentIntentId: paymentIntentId,
                                primarySessionId: sessionId,
                                transactionAmount: transactionAmount,
                                linkedToPrimaryPayment: true
                            };

                            const requestBody = {
                                event_payment_done: "1",
                                firstName: memberFirstName,
                                lastName: memberLastName,
                                phone: member.phone || "",
                                email: member.email || "",
                                comments: attendeeComment,
                                referrerSource: bannerData?.eventDetails?.leadSource || "USSAIC-2025",
                                leadSource: bannerData?.eventDetails?.leadSource || "USSAIC-2025",
                                TCPA_Consent_Given: consentValue,
                                attendee_desc: "yes",
                                section: section,
                                eventDetails: eventDetails,
                                opted_for_text_message: 0
                            };


                            await new Promise(resolve => setTimeout(resolve, 500));

                            const response = await axios.post(
                                process.env.REACT_APP_API_EMAIL,
                                requestBody,
                                {
                                    headers: {
                                        "Content-Type": "application/json",
                                        "Accept": "application/json"
                                    },
                                    timeout: 30000
                                }
                            );


                            success = true;
                            successfulRegistrations.push(i);

                        } catch (error) {



                            if (attempts < maxAttempts) {
                                await new Promise(resolve => setTimeout(resolve, 1000));
                            }
                        }
                    }

                    if (!success) {
                        failedRegistrations.push(i);
                    }
                }
            }

            // Final summary
            const totalSuccessful = successfulRegistrations.length;
            const totalFailed = failedRegistrations.length;



            if (totalSuccessful > 0) {
                if (totalFailed > 0) {
                }
            } else {
            }

            setFormLoading(false);

        } catch (error) {

            if (error.response) {
            } else if (error.request) {
            } else {
            }

            setFormLoading(false);
        }
    };

    const CloseButton = () => (
        <div className="form-close-button-container">
            <button
                type="button"
                className="form-close-button"
                onClick={() => {
                    // Immediately reset step to prevent flash
                    setStep(1);
                    resetFormData();
                    closeForm();
                }}
                aria-label="Close"
            >
                <FaTimesCircle size={24} />
            </button>
        </div>
    );

    const lastAddedIndex = basicDetails.members.length - 1;
    const [name, setName] = useState(basicDetails.members[0].name);
    const [email, setEmail] = useState(basicDetails.members[0].email);
    const [phone, setPhone] = useState(basicDetails.members[0].phone);
    const [comment, setComment] = useState("");

    // Effect to hide the form when a modal is shown
    useEffect(() => {
        if (showSuccessModal || showFailModal || showStep1SuccessModal || showStep1FailModal) {
            setHideForm(true);
        } else {
            setHideForm(false);
        }
    }, [showSuccessModal, showFailModal, showStep1SuccessModal, showStep1FailModal]);

    // Modified serialize function to use the form ref instead of querySelector
    function serialize(form) {
        if (!form) return {};

        const formData = new FormData(form);
        const values = {};

        // Get member info from the form data or state if needed
        if (basicDetails.members && basicDetails.members.length > 0) {
            values.firstName = basicDetails.members[0].name || '';
            values.email = basicDetails.members[0].email || '';
            values.phone = basicDetails.members[0].phone || '';
        }

        return values;
    }

    useEffect(() => {
        // Create a copy of the current category array
        const updatedCategories = [...basicDetails.category];

        // Update all category values based on the ticketType
        updatedCategories.fill(ticketType === "General Admission" ? "General Admission" : "VIP");

        // Update the state with the new categories
        setBasicDetails(prevState => ({
            ...prevState,
            category: updatedCategories
        }));
    }, [ticketType]);

    const handleTicketTypeChange = (index, value) => {
        const updatedCategories = [...basicDetails.category];
        updatedCategories[index] = value;

        setBasicDetails(prevState => ({
            ...prevState,
            category: updatedCategories
        }));
    };

    const handleAddMember = () => {
        if (basicDetails.members.length < 5) {
            const members = [
                ...basicDetails.members,
                { name: "", email: "", phone: "", comment: "" },
            ];
            const lastAddedIndex = members.length - 1;
            setBasicDetails({
                ...basicDetails,
                members,
                category: [...basicDetails.category, ticketType || "General Admission"],
                lastAddedIndex,
            });
        } else {
            alert("You can only add up to 5 people.");
        }
    };

    const handleRemoveMember = () => {
        const { members, category } = basicDetails;
        if (members.length > 1) {
            const updatedMembers = [...members];
            const updatedCategory = [...category];
            updatedMembers.pop();
            updatedCategory.pop();
            const updatedLastAddedIndex = updatedMembers.length - 1;

            setBasicDetails({
                ...basicDetails,
                members: updatedMembers,
                category: updatedCategory,
                lastAddedIndex: updatedLastAddedIndex,
            });
        }
    };

    const resetFormData = () => {
        setStep(1);
        setBasicDetails({
            members: [{ name: "", email: "", phone: "", comment: "" }],
            category: [ticketType || "General Admission"],
            lastAddedIndex: 0,
        });
        setDiscountCode("");
        setDiscountCodeValid(false);
        setName("");
        setEmail("");
        setPhone("");
        setComment("");
        setConsentValue(true);
        setSelectedTickets([ticketType || "General Admission"]);
        setSelectedTier("");
        setTierValidationMessage("");
        setValidated(false);
        setStep2Validated(false);

        // Reset all Stripe-related states including both IDs
        setClientSecret(null);
        setShowStripeCheckout(false);
        setPaymentIntentId(null);
        setStripePaymentIntentId(null); // New state
        setStripeSessionId(null); // New state
        setTransactionId("");
        setStripeInitialized(false);
        setStripeError(null);
        setIsCheckoutComplete(false);
        setLeadStorageAttempted(false);
        setPaymentMonitorActive(false);
    };

    const handleFailModalClose = () => {
        // Immediately reset step to prevent flash
        setStep(1);
        setShowFailModal(false);
        setStep2Validated(false);
        // Reset form data immediately
        resetFormData();
        closeForm();
    };

    // FIXED: Handle success modal close - no URL cleanup needed
    const handleSuccessModalClose = () => {
        // Immediately reset step to prevent flash
        setStep(1);
        setShowSuccessModal(false);

        // Reset form data immediately instead of with timeout
        resetFormData();
        closeForm();
    };

    const handleStep1SuccessModalClose = () => {
        // Immediately reset step to prevent flash
        setStep(1);
        setShowStep1SuccessModal(false);
        setShowPaymentModal(false);
        resetFormData();
        closeForm();
    };

    const handleStep1FailModalClose = () => {
        // Immediately reset step to prevent flash
        setStep(1);
        setShowStep1FailModal(false);
        resetFormData();
    };

    const showTicketPhoneSoliticationConsent = () => {
        setShowClickToMore(false);
    };

    const hideTicketPhoneSoliticationConsent = () => {
        setShowClickToMore(true);
    };

    const showPhotoConsent = () => {
        setShowClickToPhoto(false);
    };

    const hidePhotoConsent = () => {
        setShowClickToPhoto(true);
    };

    // Comments validation
    const handleComments = (evt, memberIndex) => {
        // Get the raw value from the event
        const { value } = evt.target;

        // Sanitize the comment (using your utility function or inline code)
        let sanitizedValue = value;
        if (sanitizedValue) {
            // Basic comment sanitization
            sanitizedValue = sanitizedValue.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
            // Use your processCommentField function if available
            if (typeof processCommentField === 'function') {
                sanitizedValue = processCommentField(sanitizedValue);
            }
        }

        // Update the correct member's comment in the state
        const updatedMembers = [...basicDetails.members];
        updatedMembers[memberIndex].comment = sanitizedValue;
        setBasicDetails({
            ...basicDetails,


            members: updatedMembers
        });

        // Also update the comment state if this is the first member
        if (memberIndex === 0) {
            setComment(sanitizedValue);
        }
    };

    // Name validation
    const handleName = (e) => {
        let tempName = e.currentTarget.value;
        tempName = removeNonAsciiSpecialCharacters(tempName);
        if (tempName) {
            setName(tempName.trim());
            e.target.value = tempName.trim();
        } else {
            setName("");
            e.target.value = "";
        }
    };

    function trim(theStr) {
        return theStr.replace(/^\s\s*/, "").replace(/\s\s*$/, "");
    }

    function getNumbers(inVal, isDouble, removeCharsFromPrefix) {
        var retVal = "";
        var okChars = "0123456789";
        var tmpVal = trim(inVal);
        var curChar = "";
        var isDotFound = false;

        if (tmpVal !== "") {
            for (var i = 0; i < tmpVal.length; i++) {
                curChar = tmpVal.charAt(i);

                if (okChars.indexOf(curChar) !== -1) {
                    if (!isDotFound && curChar === "." && isDouble) {
                        isDotFound = true;
                        retVal = retVal + ".";
                    }

                    if (retVal === "" && removeCharsFromPrefix.indexOf(curChar) !== -1) {
                        //Do nothing
                    } else {
                        retVal += curChar;
                    }
                }
            }
        }

        return retVal;
    }

    // Phone number validation with proper formatting
    const handlePhone = (evt, memberIndex) => {
        var tmpVal = trim(getNumbers(evt.currentTarget.value, false, "01"));
        var FirstThreeDigit = tmpVal.substring(0, 3);
        var FirstSixDigit = tmpVal.substring(0, 6);

        /* Check restricted phone number patterns */
        var patt =
            /^(1[0-9][0-9])|([2-7]11)|(911)|(37[0-9])|(96[0-9])|(456)|(555)|(866)|(877)|(888)|(900)$/;
        var patt2 = /^800855$/;
        if (patt.test(FirstThreeDigit)) {
            evt.currentTarget.value = "";
            return;
        } else if (patt2.test(FirstSixDigit)) {
            evt.currentTarget.value = "";
            return;
        }

        var curChar = "";
        var areaCode = "";
        var firstThree = "";
        var lastFour = "";
        var extension = "";

        if (tmpVal !== "") {
            for (var i = 0; i < tmpVal.length; i++) {
                curChar = tmpVal.charAt(i);

                if (i < 3) areaCode += curChar;
                if (i < 6 && i > 2) firstThree += curChar;
                if (i < 10 && i > 5) lastFour += curChar;
                if (i > 9) extension += curChar;
            }
        }

        var retVal = "";

        if (areaCode !== "" && firstThree === "") retVal += areaCode;
        else if (areaCode !== "" && firstThree !== "")
            retVal += "(" + areaCode + ")";

        if (firstThree !== "") retVal += " " + firstThree;

        if (lastFour !== "") retVal += "-" + lastFour;

        if (extension !== "") retVal += " x" + extension;

        // Update the correct member's phone using the passed memberIndex
        const updatedMembers = [...basicDetails.members];
        updatedMembers[memberIndex].phone = retVal;
        setBasicDetails({
            ...basicDetails,
            members: updatedMembers
        });

        // Also update the phone state if this is the first member
        if (memberIndex === 0) {
            setPhone(retVal);
        }
    };

    const handleEmail = (evt) => {
        let tempEmailAddress = evt.currentTarget.value;
        if (tempEmailAddress) {
            setEmail(tempEmailAddress.trim());
            // Check if it's a government email and General Admission
            const isGov = isGovernmentEmail(tempEmailAddress.trim());
            const isGA = basicDetails.category[0] === "General Admission";
            setShowGovMessage(isGov && isGA);
        } else {
            setEmail("");
            setShowGovMessage(false);
        }
    };

    // Calculate total amount correctly by using the ticket prices
    const calculateTotalAmount = () => {

        

        let total = 0;
        for (let i = 0; i < basicDetails.category.length; i++) {
            const category = basicDetails.category[i];
            // Add the ticket price for this category to the total
            total += parseInt(ticketPrices[category] || 0);
        }

        if (!isFreeRegistration && discountCode === "RHYTHM25") {
            total = total * 0.75; 
        }

        return total;
    };

    const handleMemberInputChange = (index, event) => {
        const { name, value } = event.target;
        const members = [...basicDetails.members];

        // Special handling based on input field type
        if (name === "phone") {
            // Phone is handled by handlePhone function
            handlePhone(event, index);
        } else if (name === "comment") {
            // Comment is handled by handleComments function
            handleComments(event, index);
        } else {
            // Standard handling for other fields
            members[index][name] = value;
            setBasicDetails({ ...basicDetails, members });

            // Update state variables for first member
            if (index === 0) {
                if (name === "name") setName(value);
                if (name === "email") {
                    setEmail(value);
                    // Check if it's a government email and General Admission
                    const isGov = isGovernmentEmail(value);
                    const isGA = basicDetails.category[0] === "General Admission";
                    setShowGovMessage(isGov && isGA);
                }
            }
        }
    };

    const handlePreviousStep = () => {
        setStep(step - 1);
        // Reset Stripe checkout if going back from payment step
        if (step === 2) {
            setClientSecret(null);
            setShowStripeCheckout(false);
            setPaymentProcessing(false);
            setStripeInitialized(false);
            setStripeError(null);
            setIsCheckoutComplete(false);
            setLeadStorageAttempted(false);
            setPaymentMonitorActive(false);
        }
    };

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleDiscountCodeChange = async (e) => {
        const code = e.target.value.toUpperCase();
        setDiscountCode(code);
        setApplyClicked(false);


    };


    useEffect(() => {
        // Set initial ticket type
        if (ticketType) {
            const initialCategory = Array(basicDetails.members.length).fill(ticketType);
            setBasicDetails(prev => ({
                ...prev,
                category: initialCategory
            }));
            setSelectedTickets([ticketType]);
        }
    }, [ticketType]);

    useEffect(() => {
        const validCode = couponCodes.includes(discountCode);
        setDiscountCodeValid(validCode || false);

        // List of promo codes that qualify for free registration
        const freePromoCodes = ["TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"];

        // Check if the discount code qualifies for free registration
        if (validCode && freePromoCodes.includes(discountCode)) {
            setIsFreeRegistration(true);
        } else {
            setIsFreeRegistration(false);
        }
    }, [discountCode, couponCodes]);

    // UPDATED: Get validation message with free registration info
    const getValidationMessage = () => {
        const freePromoCodes = ["TBCVIP", "VBCVIP", "ANGELAIVIP", "AIINNOVIP", "RHYTHM"];

        if (discountCode === "") {
            return (
                <>
                </>
            );

        }

        if (discountCodeValid && discountCode === "RHYTHM25") {
            return `Promo code "${discountCode}" is valid! You get a 25% discount on your registration.`;
        }

        if (discountCodeValid && freePromoCodes.includes(discountCode)) {
            return `Promo code "${discountCode}" is valid! Your registration will be free.`;
        }

        return (
            <>
                Promo code "<strong>{discountCode}</strong>" is invalid.
                <br />
                Please try another code.
            </>
        );
    };




    // FIXED: Initialize Stripe Checkout - simplified for your Lambda backend
    const initializeStripeCheckout = async () => {
        try {
            setPaymentProcessing(true);
            setStripeError(null);
            setIsCheckoutComplete(false);
            setLeadStorageAttempted(false);

            // Validate environment variables
            if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
                throw new Error("Stripe publishable key not configured");
            }

            if (!process.env.REACT_APP_STRIPE_CHECKOUT_URL) {
                throw new Error("Stripe checkout URL not configured");
            }

            if (!stripePromise) {
                throw new Error("Stripe.js failed to initialize");
            }

            // Calculate total amount in cents (Stripe requires cents)
            const totalAmount = calculateTotalAmount() * 100;

            // Create customer info from form data
            const firstMember = basicDetails.members[0];
            let customerName = firstMember.name || '';
            let customerEmail = firstMember.email || '';

            // FIXED: Simplified body to match your Lambda expectations
            const body = {
                price: totalAmount,
                eventName: bannerData?.eventDetails?.formEventName || "USSAIC",
                eventDescription: "Payment for " + (bannerData?.eventDetails?.formEventName || "USSAIC Event"),
                customerName: customerName,
                customerEmail: customerEmail,
                domain: window.location.origin
            };


            const response = await axios({
                method: 'post',
                url: process.env.REACT_APP_STRIPE_CHECKOUT_URL,
                data: body,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Origin': window.location.origin
                },
                timeout: 15000 // 15 second timeout
            });



            if (!response || !response.data) {
                throw new Error("Empty response from payment server");
            }

            // FIXED: Handle the client secret response from your Lambda
            let clientSecretValue;

            // Your Lambda returns the client_secret directly as a string
            if (typeof response.data === 'string') {
                clientSecretValue = response.data;
            } else if (response.data.client_secret) {
                clientSecretValue = response.data.client_secret;
            } else if (response.data.clientSecret) {
                clientSecretValue = response.data.clientSecret;
            } else {
                throw new Error("Invalid response from payment server - no client secret found");
            }

            // Validate client secret format
            if (!clientSecretValue || typeof clientSecretValue !== 'string') {
                throw new Error("Invalid client secret received from server");
            }

            // Additional validation: client secret should start with expected prefix
            if (!clientSecretValue.includes('cs_') && !clientSecretValue.includes('pi_')) {
                throw new Error("Client secret format validation failed");
            }


            // Set client secret and extract payment intent ID if available
            setClientSecret(clientSecretValue);

            // Extract payment intent ID from checkout session client secret
            if (clientSecretValue.includes('_secret_')) {
                const paymentId = clientSecretValue.split('_secret_')[0];
                setPaymentIntentId(paymentId);
            }

            // Important: Set this flag to true to show the checkout
            setShowStripeCheckout(true);
            setPaymentProcessing(false);

            return true;
        } catch (err) {

            // More detailed error logging
            if (err.response) {
                setStripeError(`Payment server error (${err.response.status}): ${err.message}`);
            } else if (err.request) {
                setStripeError("No response from payment server. Please check your connection.");
            } else {
                setStripeError(err.message || "Failed to initialize payment");
            }

            setPaymentProcessing(false);
            return false;
        }
    };

    // FIXED: Enhanced Stripe payment success handler with comprehensive logging
    const handleStripePaymentSuccess = (result) => {

        // Extract both Session ID and Payment Intent ID from result
        let sessionId = null;
        let paymentIntentId = null;

        // Try to extract Session ID from various possible locations
        if (result && result.checkout_session && result.checkout_session.id) {
            sessionId = result.checkout_session.id;
        } else if (result && result.session && result.session.id) {
            sessionId = result.session.id;
        } else if (result && result.sessionId) {
            sessionId = result.sessionId;
        }

        // Try to extract Payment Intent ID from various possible locations
        if (result && result.payment_intent && result.payment_intent.id) {
            paymentIntentId = result.payment_intent.id;
        } else if (result && result.paymentIntent && result.paymentIntent.id) {
            paymentIntentId = result.paymentIntent.id;
        } else if (result && typeof result === 'string' && result.startsWith('pi_')) {
            paymentIntentId = result;
        } else if (clientSecret && clientSecret.includes('_secret_')) {
            // Extract from client secret if not available in result
            paymentIntentId = clientSecret.split('_secret_')[0];
        }

        // Store both IDs in state
        if (sessionId) {
            setStripeSessionId(sessionId);
            setTransactionId(sessionId); // Keep this for backward compatibility
        }
        if (paymentIntentId) {
            setStripePaymentIntentId(paymentIntentId);
            setPaymentIntentId(paymentIntentId); // Update existing state
        }


        // Trigger the success flow with both IDs
        triggerSuccessFlow(paymentIntentId, sessionId);
    };

    const handleStep1Submit = async (e) => {
        e.preventDefault();
        const form = e.currentTarget;
    
        if (!form.checkValidity()) {
            e.stopPropagation();
            setValidated(true);
            return;
        }
    
        setValidated(true);
    
        const leadSource = bannerData?.eventDetails?.leadSource || "USSAIC-2025";
        
        // Check if primary attendee has government email and is General Admission
        const primaryEmail = basicDetails.members[0]?.email;
        const primarySection = basicDetails.category[0];
        const hasGovEmail = isGovernmentEmail(primaryEmail);
        const isGeneralAdmission = primarySection === "General Admission";
        
        // If government email AND General Admission, process as free registration
        if (hasGovEmail && isGeneralAdmission) {
            setFormLoading(true);
    
            const failedSubmissions = [];
            let successCount = 0;
    
            // Retry wrapper
            const submitWithRetry = async (data, retries = 2) => {
                for (let attempt = 0; attempt <= retries; attempt++) {
                    try {
                        await axios.post(process.env.REACT_APP_API_EMAIL, data, {
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json"
                            },
                            timeout: 30000
                        });
                        return true;
                    } catch (err) {
                        if (attempt === retries) return false;
                    }
                }
            };
    
            for (let i = 0; i < basicDetails.members.length; i++) {
                const member = basicDetails.members[i];
                const fullName = member.name;
                let firstName = "", lastName = "";
    
                if (fullName.includes(" ")) {
                    const parts = fullName.split(" ");
                    firstName = parts[0];
                    lastName = parts.slice(1).join(" ");
                } else {
                    firstName = fullName;
                }
    
                const section = basicDetails.category[i];
                const eventDetails = {
                    name: bannerData?.eventDetails?.formEventName || "USSAIC",
                    date: bannerData?.eventDetails?.eventDate || "July 7, 2025",
                    time: bannerData?.eventDetails?.eventTime || "9 AM - 5 PM ET",
                    address: bannerData?.eventDetails?.formFullAddress,
                    fromEmailAddress: bannerData?.eventDetails?.fromEmailAddress || "confirmation@celligence.com"
                };
    
                const requestBody = {
                    firstName: firstName,
                    lastName: lastName,
                    phone: member.phone,
                    email: member.email,
                    comments: member.comment ? `${member.comment}. Government email - Free registration` : "Government email - Free registration",
                    leadSource: leadSource,
                    TCPA_Consent_Given: consentValue,
                    referrerName: eventName,
                    company_name: "GOV_EMP_FREE",
                    attendee_desc: "yes",
                    referrerSource: leadSource,
                    section: section,
                    eventDetails: eventDetails
                };
    
                const success = await submitWithRetry(requestBody);
    
                if (success) {
                    successCount++;
                } else {
                    failedSubmissions.push({
                        name: member.name,
                        email: member.email
                    });
                }
            }
    
            try {
                if (failedSubmissions.length === 0) {
                    setShowStep1SuccessModal(true);
                } else {
                    setShowStep1FailModal(true);
                }
            } catch (error) {
                setShowStep1FailModal(true);
            } finally {
                setFormLoading(false);
            }
            return;
        }
    
        // If promo code is not entered, skip validation and move to payment
        if (!discountCode) {
            setStep(2);
            return;
        }
    
        const promoResult = await validatePromoCode(discountCode, leadSource);
        setPromoCodeMessage(promoResult?.message);
    
        if (!promoResult?.isValid) {
            setStep(2); // Invalid promo code, continue to payment
            return;
        }
    
        if (discountCode === "RHYTHM25") {
            try {
                await reducePromoCodeCount(discountCode, leadSource, basicDetails.members.length);
                setPromoCodeMessage(promoResult.message);
            } catch (error) {
                
            }
        
            setStep(2); 
            return;
        }
    
        // Free code path
        if (promoResult.isFree) {
            setFormLoading(true);
    
            const failedSubmissions = [];
            let successCount = 0;
    
            // Retry wrapper
            const submitWithRetry = async (data, retries = 2) => {
                for (let attempt = 0; attempt <= retries; attempt++) {
                    try {
                        await axios.post(process.env.REACT_APP_API_EMAIL, data, {
                            headers: {
                                "Content-Type": "application/json",
                                "Accept": "application/json"
                            },
                            timeout: 30000
                        });
                        return true; // Success
                    } catch (err) {
                        if (attempt === retries) return false;
                    }
                }
            };
    
            for (let i = 0; i < basicDetails.members.length; i++) {
                const member = basicDetails.members[i];
                const fullName = member.name;
                let firstName = "", lastName = "";
    
                if (fullName.includes(" ")) {
                    const parts = fullName.split(" ");
                    firstName = parts[0];
                    lastName = parts.slice(1).join(" ");
                } else {
                    firstName = fullName;
                }
    
                const section = discountCode === "SWMCSTAFF" ? "Staff" : basicDetails.category[i];
                const eventDetails = {
                    name: bannerData?.eventDetails?.formEventName || "USSAIC",
                    date: bannerData?.eventDetails?.eventDate || "July 7, 2025",
                    time: bannerData?.eventDetails?.eventTime || "9 AM - 5 PM ET",
                    address: bannerData?.eventDetails?.formFullAddress,
                    fromEmailAddress: bannerData?.eventDetails?.fromEmailAddress || "confirmation@celligence.com"
                };
    
                const requestBody = {
                    firstName: firstName,
                    lastName: lastName,
                    phone: member.phone,
                    email: member.email,
                    comments: member.comment || "",
                    leadSource: leadSource,
                    TCPA_Consent_Given: consentValue,
                    referrerName: eventName,
                    company_name: discountCode,
                    attendee_desc: "yes",
                    referrerSource: leadSource,
                    section: section,
                    eventDetails: eventDetails
                };
    
                const success = await submitWithRetry(requestBody);
    
                if (success) {
                    successCount++;
                } else {
                    failedSubmissions.push({
                        name: member.name,
                        email: member.email
                    });
                }
            }
    
            try {
                if (successCount > 0) {
                    await reducePromoCodeCount(discountCode, leadSource, successCount);
                }
    
                if (failedSubmissions.length === 0) {
                    setShowStep1SuccessModal(true);
                } else {
                    setShowStep1FailModal(true);
                }
            } catch (error) {
                setShowStep1FailModal(true);
            } finally {
                setFormLoading(false);
            }
    
        } else {
            // Valid promo (not free): move to payment
            setStep(2);
        }
    };
    
    
    



    // FIXED: Improved Stripe checkout loading component with better styling
    const StripeCheckoutLoading = () => (
        <div className="text-center py-4" style={{ padding: "40px 0" }}>
            <Spinner animation="border" variant="primary" style={{ width: "3rem", height: "3rem" }} />
            <p className="mt-3 mb-0">Preparing secure payment form...</p>
            <small className="mt-2 d-block text-muted">This may take a few seconds. Please do not refresh the page.</small>
        </div>
    );

    // FIXED: Improved Stripe checkout error component with better styling
    const StripeCheckoutError = () => (
        <div className="text-center py-4" style={{ padding: "40px 0" }}>
            <div style={{ color: "red", fontSize: "24px", marginBottom: "20px" }}>
                <FaTimes />
            </div>
            <p style={{ color: "red", fontWeight: "bold" }}>{stripeError || "Payment form could not be loaded"}</p>
            <p className="text-muted mb-3">Please check your internet connection and try again.</p>
            <Button
                className="retry-payment-btn mt-2"
                variant="outline-primary"
                onClick={initializeStripeCheckout}
                disabled={paymentProcessing}
            >
                {paymentProcessing ? (
                    <>
                        <Spinner
                            animation="border"
                            aria-hidden="true"
                            as="span"
                            role="status"
                            size="sm"
                        />{" "}
                        <span>Retrying...</span>
                    </>
                ) : (
                    <span>Try Again</span>
                )}
            </Button>


        </div>
    );

    return (
        <>
            {/* Step 1 Success modal */}
            <Modal
                size="md"
                centered
                id="success-modal"
                backdrop="static"
                show={showStep1SuccessModal}
                onHide={handleStep1SuccessModalClose}
            >
                <Modal.Header closeButton onClick={handleStep1SuccessModalClose} />
                <Modal.Body className="text-center mb-3">
                    <FaCheck className="success-fa" />
                    <p className="payment-modal-body">
                        Thank you for Registering up!
                    </p>
                    <p className="payment-modal-body">
                        Your exclusive Digital Art link will be sent to you shortly, giving you access to this transformative event.<br />We're thrilled to have you with us!
                    </p>
                    <p className="payment-modal-body">
                        If you have any questions please email us at{" "}
                        <a href={`mailto:${successData.contactEmail || "confirmation@celligence.com"}`}>
                            {successData.contactEmail || "confirmation@celligence.com"}
                        </a>.
                    </p>
                </Modal.Body>
            </Modal>

            {/* Step 1 Fail modal */}
            <Modal
                size="md"
                centered
                id="success-modal"
                backdrop="static"
                show={showStep1FailModal}
                onHide={handleStep1FailModalClose}
            >
                <Modal.Header closeButton onClick={handleStep1FailModalClose} />
                <Modal.Body className="text-center mb-3">
                    <p className="payment-modal-body">
                        Due to technical difficulties your request could not be processed.
                        Please contact us at&nbsp;
                        <a href={`mailto:${successData.contactEmail || "confirmation@celligence.com"}`}>
                            {successData.contactEmail || "confirmation@celligence.com"}
                        </a>.
                    </p>
                </Modal.Body>
            </Modal>

            {/* Payment Success Modal */}
            <Modal
                size="md"
                centered
                id="payment-success-modal"
                backdrop="static"
                show={showSuccessModal}
                onHide={handleSuccessModalClose}
            >
                <Modal.Header closeButton onClick={handleSuccessModalClose} />
                <Modal.Body className="text-center mb-3">
                    <FaCheck className="success-fa" />
                    <h4 className="payment-modal-head">
                        {successData.title || "Payment Successful!"}
                    </h4>
                    <p className="payment-modal-body">
                        Thank you for purchasing the tickets. Your payment was successful.
                    </p>
                    <p className="payment-modal-body">
                        Your exclusive Digital Art link will be sent to you shortly, giving you access to this transformative event.<br />We're thrilled to have you with us!
                    </p>
                    <p className="payment-modal-body">
                        If you have any questions please email us at{" "}
                        <a href={`mailto:${successData.contactEmail || "confirmation@celligence.com"}`}>
                            {successData.contactEmail || "confirmation@celligence.com"}
                        </a>.
                    </p>
                </Modal.Body>
            </Modal>

            {/* Payment Fail Modal */}
            <Modal
                size="md"
                centered
                id="payment-fail-modal"
                backdrop="static"
                show={showFailModal}
                onHide={handleFailModalClose}
            >
                <Modal.Header closeButton onClick={handleFailModalClose} />
                <Modal.Body className="text-center mb-3">
                    <FaTimes className="fail-fa" />
                    <h4 className="payment-modal-head">Payment failed</h4>
                    <p className="payment-modal-body">
                        Error processing payment. Please check your connection and try again.
                    </p>
                    <p className="payment-modal-body">
                        If you have any questions please email us at{" "}
                        <a href={`mailto:${successData.contactEmail || "confirmation@celligence.com"}`}>
                            {successData.contactEmail || "confirmation@celligence.com"}
                        </a>.
                    </p>
                </Modal.Body>
            </Modal>

            {/* Main form container */}
            {!hideForm && (
                <div className="main-div pb-5" id="payment-modal-top" ref={mainContainerRef}>
                    {showCloseButton && <CloseButton />}
                    {/* Basic details form: Step 1 */}
                    {step === 1 && (
                        <div>
                            <Container className="claim-container">
                                <div className="claim-head">
                                    <strong>Claim Your Spot Now!</strong>
                                </div>
                            </Container>

                            <Container className="form-container-primary">
                                <Row>
                                    <div className="ticketFormHead">
                                        <Col
                                            xs={12}
                                            sm={12}
                                            md={12}
                                            lg={12}
                                            xl={12}
                                            className="step1-col1"
                                        >
                                            <p>
                                                <span className="subhead">
                                                    <strong>Your Ticket Info</strong>
                                                </span>
                                                <br />
                                                <span className="subhead2">Who Is Attending?</span>
                                            </p>
                                        </Col>
                                    </div>
                                </Row>
                                <Row>
                                    <Form
                                        noValidate
                                        validated={validated}
                                        onSubmit={handleStep1Submit}
                                        autoComplete="off"
                                        method="POST"
                                        id="signupForm"
                                        ref={formRef}
                                    >
                                        {basicDetails.members.map((member, index) => (
                                            <div key={index}>
                                                {basicDetails.members.length > 1 && (
                                                    <p className="mt-3 mb-3">Attendee {index + 1}</p>
                                                )}
                                                <Col sm={12} xs={12} md={12} lg={12} xl={12}>
                                                    <Form.Group className="form.group" controlId={`name-${index}`}>
                                                        <InputGroup className="mb-3">
                                                            <Form.Control
                                                                aria-label="Full Name"
                                                                placeholder="Full Name"
                                                                pattern="^[a-zA-Z\s]+$"
                                                                type="text"
                                                                name="name"
                                                                required
                                                                value={member.name}
                                                                onChange={(event) =>
                                                                    handleMemberInputChange(index, event)
                                                                }
                                                                onBlur={handleName}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                <small>Please enter a valid Full Name.</small>
                                                            </Form.Control.Feedback>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={12} xs={12} md={12} lg={12} xl={12}>
                                                    <Form.Group className="form.group" controlId={`email-${index}`}>
                                                        <InputGroup className="mb-3">
                                                            <Form.Control
                                                                aria-label="Email Address"
                                                                placeholder="Email Address"
                                                                type="email"
                                                                required
                                                                name="email"
                                                                pattern="^[a-zA-Z0-9_\-\.]+@[a-zA-Z0-9\-]+(?:\.[a-zA-Z0-9\-]{2,64})$"
                                                                value={member.email}
                                                                onChange={(event) =>
                                                                    handleMemberInputChange(index, event)
                                                                }
                                                                onBlur={handleEmail}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                <small>Please enter a valid Email Address.</small>
                                                            </Form.Control.Feedback>
                                                        </InputGroup>
                                                        {index === 0 && showGovMessage && (
                                                            <p style={{ color: 'green', fontSize: '14px', marginTop: '5px', marginBottom: '10px' }}>
                                                                ✓ As a government employee, you are eligible for free registration.
                                                            </p>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={12} xs={12} md={12} lg={12} xl={12}>
                                                    <Form.Group className="form.group" controlId={`phone-${index}`}>
                                                        <InputGroup className="mb-3">
                                                            <Form.Control
                                                                aria-label="Phone Number"
                                                                placeholder="Phone Number"
                                                                maxLength={14}
                                                                type="text"
                                                                name="phone"
                                                                value={member.phone}
                                                                onChange={(event) => handleMemberInputChange(index, event)}
                                                            />
                                                            <Form.Control.Feedback type="invalid">
                                                                <small>Please enter a valid Phone number.</small>
                                                            </Form.Control.Feedback>
                                                        </InputGroup>
                                                    </Form.Group>
                                                </Col>
                                                <Col sm={12} xs={12} md={12} lg={12} xl={12}>
                                                    {index === 0 && (
                                                        <Form.Group className="form.group" controlId={`comment-${index}`}>
                                                            <InputGroup className="mb-3">
                                                                <Form.Control
                                                                    aria-label="Comment"
                                                                    placeholder="Post a comment"
                                                                    as="textarea"
                                                                    name="comment"
                                                                    value={member.comment || ""}
                                                                    onChange={(event) => handleMemberInputChange(index, event)}
                                                                />
                                                                <Form.Control.Feedback type="invalid">
                                                                    <small>Please enter a valid comment.</small>
                                                                </Form.Control.Feedback>
                                                            </InputGroup>
                                                        </Form.Group>
                                                    )}
                                                </Col>
                                                <hr style={{ borderTop: "1px solid #ccc" }}></hr>
                                            </div>
                                        ))}
                                        <div className="buttons">
                                            <p className="more-members">
                                                If you want to purchase more tickets, please click on the
                                                "Add More Attendees" button below.
                                            </p>
                                            <Button
                                                type="button"
                                                style={{ fontSize: "smaller" }}
                                                className="added-member"
                                                onClick={handleAddMember}
                                            >
                                                Add More Attendees
                                            </Button>
                                            {basicDetails.members.length > 1 && (
                                                <Button
                                                    type="button"
                                                    style={{ fontSize: "smaller" }}
                                                    className="btn-danger mx-4 mb-3"
                                                    onClick={handleRemoveMember}
                                                >
                                                    Remove Last Added Attendee
                                                </Button>
                                            )}
                                        </div>

                                        {/* ADDED: Discount code section */}


                                        <div className="discount-section pt-3 pb-1 align-items-center">
                                            <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 position-relative">
                                                <div className="discount-input-wrapper position-relative">
                                                    <input
                                                        className={`discount-field pe-5 ${discountCodeTouched
                                                            ? isValidPromo === true
                                                                ? "is-valid"
                                                                : "is-invalid"
                                                            : ""
                                                            }`}
                                                        type="text"
                                                        placeholder="Have a Promo code? Apply here"
                                                        onChange={handleDiscountCodeChange}
                                                        // onBlur={handleDiscountCodeBlur}
                                                        value={discountCode}
                                                    />

                                                    <span
                                                        className={`apply-button ${discountCode ? 'active' : ''} ${isCheckingCode ? 'disabled' : ''}`}
                                                        onClick={!isCheckingCode ? handleApplyClick : null}
                                                    >
                                                        {isCheckingCode ? (
                                                            <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                                        ) : (
                                                            "APPLY"
                                                        )}
                                                    </span>
                                                </div>

                                                {applyClicked && showValidationMessage && (
                                                    <p
                                                    className={
                                                        discountCode === ""
                                                            ? "validation-message"
                                                            : isValidPromo === true
                                                                ? "valid-message"
                                                                : isValidPromo === false
                                                                    ? "invalid-message invalid-feedback-changes"
                                                                    : "changes-new-validation-message"
                                                    }
                                                    >
                                                        {parse(promoCodeMessage)}
                                                    </p>
                                                )}


                                            </div>
                                        </div>


                                        {/* <div className="d-flex discount-section pt-3">
                                        
                                            <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
                                                <input
                                                    className={`discount-field ${discountCodeTouched
                                                            ? discountCodeValid === true
                                                                ? "is-valid"
                                                                : "is-invalid"
                                                            : ""
                                                        }`}
                                                    type="text"
                                                    placeholder="Have a Promo code? Type here"
                                                    onChange={handleDiscountCodeChange}
                                                    onBlur={handleDiscountCodeBlur}
                                                    value={discountCode}
                                                />
                                                <p
                                                    className={`${discountCode === ""
                                                            ? "validation-message"
                                                            : discountCodeValid === true
                                                                ? "valid-message"
                                                                : discountCodeValid === false
                                                                    ? "invalid-message invalid-feedback-changes"
                                                                    : ""
                                                        }`}
                                                >
                                                    {getValidationMessage()}
                                                </p>
                                            </div>
                                        </div> */}


                                        {/* <Col sm={12} xs={12} md={12} lg={12} xl={12}>
    <Form.Group className="form.group" controlId="coupon-code">
        <InputGroup className="mb-3">
            <Form.Control
                aria-label="Coupon Code"
                placeholder="Enter coupon code"
                type="text"
                name="couponCode"
                value={discountCode}
                onChange={handleDiscountCodeChange}
                onBlur={handleDiscountCodeBlur} 
                isValid={discountCodeTouched && discountCodeValid === true}
                isInvalid={discountCodeTouched && discountCodeValid === false}
            />
            <Form.Control.Feedback type="valid">
                {discountCodeTouched && discountCodeValid === true && getValidationMessage()}
            </Form.Control.Feedback>
            <Form.Control.Feedback type="invalid">
                {discountCodeTouched && discountCodeValid === false && getValidationMessage()}
            </Form.Control.Feedback>
        </InputGroup>
    </Form.Group>
</Col> */}




                                        <div className="photoconsentpchanges" >
                                            {showTicketClickToPhoto ? (
                                                <p id="photoConsent" className="consent-photo1 pb-0" >
                                                    {parse(
                                                        'By pressing \'Continue\', (i) you acknowledge that the data you entered above will be recorded, and you also agree to Celligence, LLC.\'s ("Celligence") <a href="https://www.celligence.com/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="https://www.celligence.com/privacy" target="_blank" rel="noopener noreferrer">Security and Privacy Policy</a>, which you may access by clicking on those terms; (ii) give comprehensive, royalty free, and blanket permission to be recorded at the event.'
                                                    )}
                                                    {" "}
                                                    <span
                                                        id="ticketsshowPhotoConsent"
                                                        className="consentLink1 ticketsshowConsentText"
                                                        title="Show Photo Terms and Conditions"
                                                        onClick={showPhotoConsent}
                                                    >
                                                        (click to expand recording terms & conditions)
                                                    </span>
                                                </p>
                                            ) : (
                                                <p id="photoConsent" className="consent-photo1 pb-0" >
                                                    {parse(
                                                        'By pressing \'Continue\', (i) you acknowledge that the data you entered above will be recorded, and you also agree to Celligence, LLC.\'s ("Celligence") <a href="https://www.celligence.com/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and <a href="https://www.celligence.com/privacy" target="_blank" rel="noopener noreferrer">Security and Privacy Policy</a>, which you may access by clicking on those terms; (ii) give comprehensive, royalty free, and blanket permission to be recorded at the event.'
                                                    )}{" "}
                                                    <span
                                                        id="ticketshideConsentPhoto"
                                                        className="PhotoconsentLink1 ticketshideConsentText"
                                                        title="Hide Photo Terms and Conditions"
                                                        onClick={hidePhotoConsent}
                                                    >
                                                        (click to hide recording terms & conditions)
                                                    </span>
                                                </p>
                                            )}
                                        </div>

                                        {!showTicketClickToPhoto && (
                                            <p id="photoConsent" className="consent-photo1 pb-0">
                                                I have granted royalty free permission to Celligence and its successors, assignees and licensees to use my name, voice and/or likeness ("Appearance") in the photographing, recording, and/or filming at this event in whole or in part, by any and all means, media, devices, processes and technology now or hereafter known or devised for use and distribution throughout the universe in perpetuity. I hereby release Celligence, its successors, assignees and licensees from any and all claims and demands arising out of or in connection with such use including, without limitation, any and all claims for invasion of privacy, infringement of your right of publicity, defamation (including libel and slander) and any other personal and/or property rights. In no event may I terminate the rights granted hereunder, or seek or obtain injunctive relief or other equitable relief with respect to the rights granted hereunder.
                                            </p>
                                        )}

                                        <div>
                                            <Button
                                                type="submit"
                                                className="finalize-btn mt-3"
                                                id="finalize-order-btn"
                                                disabled={formLoading}
                                            >
                                                {formLoading ? (
                                                    <>
                                                        <Spinner
                                                            animation="border"
                                                            aria-hidden="true"
                                                            as="span"
                                                            role="status"
                                                            size="sm"
                                                        />{" "}
                                                        <strong>Please wait...</strong>
                                                    </>
                                                ) : (
                                                    <strong>Continue</strong>
                                                )}
                                            </Button>
                                        </div>


                                    </Form>
                                </Row>
                            </Container>
                        </div>
                    )}

                    {/* Order summary screen: Step 2*/}
                    {step === 2 && (
                        <div>
                            <Button
                                title="Back to Attendee Details"
                                className="back-btn-attendee"
                                id="back-to-details-btn"
                                type="button"
                                onClick={handlePreviousStep}
                            >
                                <FaArrowLeft style={{ marginRight: '5px', marginTop: '-2px' }} />
                                <strong>Edit Attendee Details</strong>
                            </Button>
                            <Container className="form-container-secondary">

                                {/* Order summary section - unchanged */}
                                {/* <div>
                                    <table className="main-table">
                                    
                                    </table>

                                    <div className="button-container-attendee">
                                        <Button
                                            title="Back to Attendee Details"
                                            className="back-btn-attendee"
                                            id="back-to-details-btn"
                                            type="button"
                                            onClick={handlePreviousStep}
                                        >
                                            <FaArrowLeft style={{ marginRight: '5px' }} />
                                            <strong>Edit Attendee Details</strong>
                                        </Button>

                                        <Row className="amt-row">
                                            <Col className="amt-col" xs={12} sm={12} md={12} lg={12} xl={12}>
                                                <div className="total-amt">
                                                    <b>
                                                        <i>Order Total </i> ${calculateTotalAmount()}.00
                                                    </b>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </div>  */}

                                {/* ENHANCED STRIPE SECTION WITH MULTIPLE SUCCESS DETECTION METHODS */}
                                <div className="stripe-payment-section">
                                    <div id="checkout" className="stripe-checkout-payment">
                                        {paymentProcessing ? (
                                            <StripeCheckoutLoading />
                                        ) : stripeError ? (
                                            <StripeCheckoutError />
                                        ) : !stripeReady ? (
                                            <div className="text-center mt-4 p-4">
                                                <p className="text-danger">Error: Payment system did not initialize correctly</p>
                                                <p>Please make sure you have a stable internet connection and try again.</p>
                                                <Button
                                                    onClick={() => window.location.reload()}
                                                    variant="secondary"
                                                    className="mt-3"
                                                >
                                                    Reload Page
                                                </Button>
                                            </div>
                                        ) : clientSecret && showStripeCheckout ? (
                                            <div style={{
                                                height: "650px",
                                                width: "100%",
                                                position: "relative",
                                                border: "none",
                                                margin: "0",
                                                padding: "0",
                                                overflow: "visible",
                                                zIndex: 999,
                                                background: "transparent"
                                            }}>
                                                <EmbeddedCheckoutProvider
                                                    stripe={stripePromise}
                                                    options={{
                                                        clientSecret,
                                                        onComplete: handleStripePaymentSuccess
                                                    }}
                                                >
                                                    <div id="stripe-embedded-container">
                                                        <EmbeddedCheckout
                                                            onComplete={handleStripePaymentSuccess}
                                                            onError={(error) => {
                                                                setStripeError(error.message || "An error occurred with the payment form");
                                                            }}
                                                        />
                                                    </div>
                                                </EmbeddedCheckoutProvider>
                                            </div>
                                        ) : stripeInitialized ? (
                                            <StripeCheckoutLoading />
                                        ) : (
                                            <div className="text-center p-4">
                                                {/* <Button 
                                                    onClick={initializeStripeCheckout} 
                                                    variant="primary"
                                                    size="lg"
                                                    className="mt-3"
                                                    disabled={paymentProcessing}
                                                >
                                                    {paymentProcessing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Loading Payment Form...
                                                        </>
                                                    ) : (
                                                        "Load Payment Form"
                                                    )}
                                                </Button> */}
                                            </div>
                                        )}


                                    </div>
                                </div>
                            </Container>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default PaymentForm;